import { useMemo, useState } from "react"

import {
  useAdNetworks,
  useCreateAdNetwork,
  useUpdateAdNetwork,
  useDeleteAdNetwork,
  useToggleAdNetworkEnabled,
  useNetworkAccounts,
  useUpdateNetworkAccounts,
  type AdNetwork,
} from "@/api/hooks/admin"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"

function showToast(msg: string, type: "success" | "error" = "success") {
  const el = document.createElement("div")
  el.textContent = msg
  el.className = `fixed top-4 right-4 z-[9999] rounded-lg px-4 py-3 text-sm font-medium shadow-lg ${
    type === "error"
      ? "bg-red-900/90 text-red-200 border border-red-700"
      : "bg-green-900/90 text-green-200 border border-green-700"
  }`
  document.body.appendChild(el)
  setTimeout(() => el.remove(), 3000)
}

// ---------------------------------------------------------------------------
// Network Form Modal
// ---------------------------------------------------------------------------

function NetworkFormModal({
  open,
  onClose,
  network,
}: {
  open: boolean
  onClose: () => void
  network: AdNetwork | null
}) {
  const createMut = useCreateAdNetwork()
  const updateMut = useUpdateAdNetwork()

  // Key prop on parent resets state when editing target changes
  const [name, setName] = useState(network?.name ?? "")
  const [slug, setSlug] = useState(network?.slug ?? "")
  const [baseUrl, setBaseUrl] = useState(network?.base_url ?? "")
  const [apiKey, setApiKey] = useState(network?.api_key ?? "")

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      if (network) {
        await updateMut.mutateAsync({
          id: network.id,
          name,
          slug,
          base_url: baseUrl || undefined,
          api_key: apiKey || undefined,
        })
        showToast("Network updated")
      } else {
        await createMut.mutateAsync({
          name,
          slug,
          base_url: baseUrl || undefined,
          api_key: apiKey || undefined,
        })
        showToast("Network created")
      }
      onClose()
    } catch {
      showToast("Failed to save network", "error")
    }
  }

  const isPending = createMut.isPending || updateMut.isPending

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="border-[#4a4a4a] bg-[#2d2d2d] text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{network ? "Edit Ad Network" : "Add Ad Network"}</DialogTitle>
          <DialogDescription className="text-white">
            {network ? "Update the network details." : "Create a new ad network."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Name *</label>
            <input
              className="w-full rounded border border-[#555] bg-[#3a3a3a] px-3 py-2 text-sm text-white"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Slug *</label>
            <input
              className="w-full rounded border border-[#555] bg-[#3a3a3a] px-3 py-2 text-sm text-white"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              required
              pattern="[a-z0-9-]+"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Base URL</label>
            <input
              type="url"
              className="w-full rounded border border-[#555] bg-[#3a3a3a] px-3 py-2 text-sm text-white"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">API Key</label>
            <input
              type="password"
              className="w-full rounded border border-[#555] bg-[#3a3a3a] px-3 py-2 text-sm text-white"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-[#7a808b]/45 bg-[#636b78] px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#58606c]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#687bda] disabled:opacity-50"
            >
              {isPending ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Accounts Assignment Modal
// ---------------------------------------------------------------------------

function NetworkAccountsModal({
  open,
  onClose,
  network,
}: {
  open: boolean
  onClose: () => void
  network: AdNetwork | null
}) {
  const { data, isLoading } = useNetworkAccounts(open && network ? network.slug : null)
  const updateMut = useUpdateNetworkAccounts()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [initialized, setInitialized] = useState(false)
  const [search, setSearch] = useState("")

  const displayAccounts = useMemo(() => {
    const accounts = data?.accounts ?? []
    const q = search.trim().toLowerCase()
    const filtered = q
      ? accounts.filter(
          (acc) =>
            acc.advertiser_name?.toLowerCase().includes(q) ||
            acc.advertiser_id.toLowerCase().includes(q) ||
            (acc.current_network ?? "").toLowerCase().includes(q),
        )
      : accounts

    return [...filtered].sort((a, b) => {
      const aAssigned = selected.has(a.advertiser_id)
      const bAssigned = selected.has(b.advertiser_id)
      if (aAssigned !== bAssigned) return aAssigned ? -1 : 1
      return (a.advertiser_name ?? "").localeCompare(b.advertiser_name ?? "")
    })
  }, [data?.accounts, search, selected])

  const assignedCount = useMemo(
    () => (data?.accounts ?? []).filter((acc) => selected.has(acc.advertiser_id)).length,
    [data?.accounts, selected],
  )

  // Initialize selection from server data
  if (data && !initialized) {
    const initial = new Set<string>()
    for (const acc of data.accounts) {
      if (acc.selected) initial.add(acc.advertiser_id)
    }
    setSelected(initial)
    setInitialized(true)
  }

  // Reset when modal closes
  if (!open && initialized) {
    setInitialized(false)
  }
  if (!open && search) {
    setSearch("")
  }

  function toggle(advId: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(advId)) next.delete(advId)
      else next.add(advId)
      return next
    })
  }

  async function handleSave() {
    if (!network) return
    try {
      await updateMut.mutateAsync({
        slug: network.slug,
        accountIds: Array.from(selected),
      })
      showToast("Accounts updated")
      onClose()
    } catch {
      showToast("Failed to update accounts", "error")
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[80vh] overflow-y-auto border-[#4a4a4a] bg-[#2d2d2d] text-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{network?.name} — Accounts</DialogTitle>
          <DialogDescription className="text-white">
            Select accounts to assign to this network.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="py-4 text-center text-sm text-white">Loading accounts...</p>
        ) : (
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Search by name, ID, or network..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded border border-[#555] bg-[#3a3a3a] px-3 py-2 text-sm text-white placeholder:text-[#888]"
            />
            {assignedCount > 0 && (
              <p className="text-xs text-[#8b9aff]">
                {assignedCount} account{assignedCount === 1 ? "" : "s"} assigned to{" "}
                {network?.name}
              </p>
            )}
            <div className="max-h-[50vh] space-y-1 overflow-y-auto rounded border border-[#4a4a4a] bg-[#252525] p-2">
              {displayAccounts.length === 0 ? (
                <p className="py-4 text-center text-sm text-[#888]">
                  {search.trim() ? "No accounts match your search." : "No accounts found."}
                </p>
              ) : (
                displayAccounts.map((acc, index) => {
                  const isAssigned = selected.has(acc.advertiser_id)
                  const prevAssigned =
                    index > 0 && selected.has(displayAccounts[index - 1].advertiser_id)
                  const showAssignedHeader = isAssigned && index === 0
                  const showOtherHeader = !isAssigned && prevAssigned

                  return (
                    <div key={acc.id}>
                      {showAssignedHeader && (
                        <div className="px-2 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#8b9aff]">
                          Assigned
                        </div>
                      )}
                      {showOtherHeader && (
                        <div className="mt-2 border-t border-[#444] px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-[#888]">
                          Other accounts
                        </div>
                      )}
                      <label className="flex items-center gap-2 rounded px-2 py-1 hover:bg-[#3a3a3a]">
                        <input
                          type="checkbox"
                          checked={isAssigned}
                          onChange={() => toggle(acc.advertiser_id)}
                          className="accent-[#8b9aff]"
                        />
                        <span className="text-sm">
                          {acc.advertiser_name} ({acc.advertiser_id})
                          {acc.current_network && acc.current_network !== network?.slug && (
                            <span className="ml-1 text-xs text-amber-400">
                              [{acc.current_network}]
                            </span>
                          )}
                        </span>
                      </label>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-[#7a808b]/45 bg-[#636b78] px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#58606c]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={updateMut.isPending}
            className="rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#687bda] disabled:opacity-50"
          >
            {updateMut.isPending ? "Saving..." : "Save"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Main Tab
// ---------------------------------------------------------------------------

export default function AdNetworksTab() {
  const { data, isLoading, error } = useAdNetworks()
  const deleteMut = useDeleteAdNetwork()
  const toggleMut = useToggleAdNetworkEnabled()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<AdNetwork | null>(null)
  const [accountsOpen, setAccountsOpen] = useState(false)
  const [accountsNetwork, setAccountsNetwork] = useState<AdNetwork | null>(null)

  function openCreate() {
    setEditing(null)
    setFormOpen(true)
  }

  function openEdit(n: AdNetwork) {
    setEditing(n)
    setFormOpen(true)
  }

  function openAccounts(n: AdNetwork) {
    setAccountsNetwork(n)
    setAccountsOpen(true)
  }

  async function handleDelete(n: AdNetwork) {
    if (!confirm(`Delete network "${n.name}"?`)) return
    try {
      await deleteMut.mutateAsync(n.id)
      showToast("Network deleted")
    } catch {
      showToast("Failed to delete network", "error")
    }
  }

  async function handleToggleEnabled(n: AdNetwork, enabled: boolean) {
    try {
      await toggleMut.mutateAsync({ id: n.id, is_enabled: enabled })
      showToast(enabled ? `${n.name} enabled` : `${n.name} disabled`)
    } catch {
      showToast("Failed to update network status", "error")
    }
  }

  if (error) return <p className="text-red-400">Failed to load ad networks.</p>

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      <div className="flex shrink-0 justify-end">
        <button
          onClick={openCreate}
          className="rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda]"
        >
          + Add Ad Network
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[#4a4a4a]">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 z-10 bg-[#444444] text-white">
            <tr>
              <th className="px-4 py-3 font-medium">On/Off</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Slug</th>
              <th className="px-4 py-3 font-medium">Base URL</th>
              <th className="px-4 py-3 font-medium">API Key</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#4a4a4a]">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-white">
                  Loading...
                </td>
              </tr>
            ) : !data?.data.length ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-white">
                  No ad networks found.
                </td>
              </tr>
            ) : (
              data.data.map((n) => (
                <tr key={n.id} className="transition-colors hover:bg-[#444444]">
                  <td className="px-4 py-3">
                    <label
                      className="relative inline-block h-6 w-11 cursor-pointer align-middle"
                      title={n.is_enabled ? "Disable network" : "Enable network"}
                    >
                      <input
                        type="checkbox"
                        checked={n.is_enabled}
                        disabled={toggleMut.isPending}
                        onChange={(e) => handleToggleEnabled(n, e.target.checked)}
                        className="peer sr-only"
                        aria-label={`${n.name} enabled`}
                      />
                      <span className="absolute inset-0 rounded-full bg-[#555] transition-colors peer-checked:bg-[#10b981] peer-disabled:opacity-50" />
                      <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
                    </label>
                  </td>
                  <td className="px-4 py-3 text-white">{n.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-white">{n.slug}</td>
                  <td className="max-w-[200px] truncate px-4 py-3 text-xs text-white">
                    {n.base_url || "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-white">
                    {n.api_key ? "••••••" : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-white">
                    {n.created_at ? new Date(n.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(n)}
                        className="rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda]"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => openAccounts(n)}
                        className="rounded-md border border-[#58b896]/35 bg-[#3fa781] px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#379773]"
                      >
                        Accounts
                      </button>
                      <button
                        onClick={() => handleDelete(n)}
                        className="rounded-md border border-[#d26a78]/35 bg-[#c6596a] px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#b84f60]"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <NetworkFormModal
        key={editing?.id ?? "new"}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        network={editing}
      />
      <NetworkAccountsModal
        open={accountsOpen}
        onClose={() => setAccountsOpen(false)}
        network={accountsNetwork}
      />
    </div>
  )
}
